package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.domain.model.PurchasesProductRow
import com.composables.icons.lucide.EllipsisVertical
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Pencil
import com.composables.icons.lucide.Search
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_action_edit
import portalvv.shared.generated.resources.compras_action_view
import portalvv.shared.generated.resources.compras_actions_menu
import portalvv.shared.generated.resources.compras_col_actions
import portalvv.shared.generated.resources.compras_col_consumption
import portalvv.shared.generated.resources.compras_col_measurement
import portalvv.shared.generated.resources.compras_col_product
import portalvv.shared.generated.resources.compras_col_total
import portalvv.shared.generated.resources.compras_col_unit_price
import portalvv.shared.generated.resources.compras_no_results
import portalvv.shared.generated.resources.compras_search_placeholder
import portalvv.shared.generated.resources.compras_table_empty
import portalvv.shared.generated.resources.compras_total

private const val WEIGHT_NAME = 1.8f
private const val WEIGHT_MEASUREMENT = 1.2f
private const val WEIGHT_CONSUMPTION = 1.1f
private const val WEIGHT_UNIT_PRICE = 1.1f
private const val WEIGHT_TOTAL = 1.2f
private val ACTIONS_WIDTH = 44.dp
private val TABLE_MIN_WIDTH = 900.dp

@Composable
fun PurchasesCategoryContent(
    category: PurchasesCategory,
    rows: List<PurchasesProductRow>,
    totalCents: Long,
    loading: Boolean,
    canEdit: Boolean,
    onSaveProduct: (PurchasesProductRow) -> Unit,
) {
    var query by remember(category) { mutableStateOf("") }
    var viewing by remember(category) { mutableStateOf<PurchasesProductRow?>(null) }
    var editing by remember(category) { mutableStateOf<PurchasesProductRow?>(null) }
    val filteredRows = remember(rows, query) { filterPurchasesProducts(rows, query) }

    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = stringResource(Res.string.compras_total, "R$ ${MoneyFormatter.formatCents(totalCents)}"),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        AppTextField(
            value = query,
            onValueChange = { query = it },
            placeholder = stringResource(Res.string.compras_search_placeholder),
            leadingIcon = { Icon(Lucide.Search, contentDescription = null) },
            modifier = Modifier.fillMaxWidth(),
        )
        if (loading) {
            Box(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .padding(vertical = AppSpacing.space32),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        } else if (rows.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().height(360.dp)) {
                EmptyState(message = stringResource(Res.string.compras_table_empty))
            }
        } else {
            PurchasesProductsTable(
                rows = filteredRows,
                emptyMessage = stringResource(Res.string.compras_no_results),
                canEdit = canEdit,
                onView = { viewing = it },
                onEdit = { editing = it },
            )
        }
    }

    viewing?.let { product ->
        PurchasesProductDetailsDialog(
            product = product,
            onDismiss = { viewing = null },
        )
    }

    if (canEdit) {
        editing?.let { product ->
            PurchasesProductEditDialog(
                product = product,
                onDismiss = { editing = null },
                onSave = { updated ->
                    onSaveProduct(updated)
                    editing = null
                },
            )
        }
    }
}

@Composable
private fun PurchasesProductsTable(
    rows: List<PurchasesProductRow>,
    emptyMessage: String,
    canEdit: Boolean,
    onView: (PurchasesProductRow) -> Unit,
    onEdit: (PurchasesProductRow) -> Unit,
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val tableWidth = if (maxWidth < TABLE_MIN_WIDTH) TABLE_MIN_WIDTH else maxWidth
        val scrollState = rememberScrollState()

        Box(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .horizontalScroll(scrollState),
        ) {
            Surface(
                modifier = Modifier.width(tableWidth),
                shape = AppShapes.medium,
                color = MaterialTheme.colorScheme.surface,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    TableHeader()
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    if (rows.isEmpty()) {
                        Box(
                            modifier =
                                Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = AppSpacing.space24),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                text = emptyMessage,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    } else {
                        rows.forEachIndexed { index, row ->
                            ProductRow(
                                row = row,
                                canEdit = canEdit,
                                onView = { onView(row) },
                                onEdit = { onEdit(row) },
                            )
                            if (index < rows.lastIndex) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TableHeader() {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(AppPalette.Neutral50)
                .padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        HeaderCell(
            text = stringResource(Res.string.compras_col_product),
            weight = WEIGHT_NAME,
            textAlign = TextAlign.Start,
        )
        HeaderCell(
            text = stringResource(Res.string.compras_col_measurement),
            weight = WEIGHT_MEASUREMENT,
            textAlign = TextAlign.Center,
        )
        HeaderCell(
            text = stringResource(Res.string.compras_col_consumption),
            weight = WEIGHT_CONSUMPTION,
            textAlign = TextAlign.Center,
        )
        HeaderCell(
            text = stringResource(Res.string.compras_col_unit_price),
            weight = WEIGHT_UNIT_PRICE,
            textAlign = TextAlign.Center,
        )
        HeaderCell(
            text = stringResource(Res.string.compras_col_total),
            weight = WEIGHT_TOTAL,
            textAlign = TextAlign.Center,
        )
        Box(
            modifier = Modifier.width(ACTIONS_WIDTH),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = stringResource(Res.string.compras_col_actions),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
    textAlign: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = textAlign,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun ProductRow(
    row: PurchasesProductRow,
    canEdit: Boolean,
    onView: () -> Unit,
    onEdit: () -> Unit,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        BodyCell(
            text = row.name,
            weight = WEIGHT_NAME,
            textAlign = TextAlign.Start,
        )
        BodyCell(
            text = row.measurement,
            weight = WEIGHT_MEASUREMENT,
            textAlign = TextAlign.Center,
        )
        BodyCell(
            text = MoneyFormatter.formatNumber(row.consumption),
            weight = WEIGHT_CONSUMPTION,
            textAlign = TextAlign.Center,
        )
        BodyCell(
            text = "R$ ${MoneyFormatter.formatCents(row.unitPriceCents)}",
            weight = WEIGHT_UNIT_PRICE,
            textAlign = TextAlign.Center,
        )
        BodyCell(
            text = "R$ ${MoneyFormatter.formatCents(row.totalCents)}",
            weight = WEIGHT_TOTAL,
            textAlign = TextAlign.Center,
        )
        Box(
            modifier = Modifier.width(ACTIONS_WIDTH),
            contentAlignment = Alignment.Center,
        ) {
            RowActions(
                canEdit = canEdit,
                onView = onView,
                onEdit = onEdit,
            )
        }
    }
}

@Composable
private fun RowScope.BodyCell(
    text: String,
    weight: Float,
    textAlign: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        textAlign = textAlign,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun RowActions(
    canEdit: Boolean,
    onView: () -> Unit,
    onEdit: () -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }

    Box {
        IconButton(
            onClick = { expanded = true },
            modifier = Modifier.size(28.dp),
        ) {
            Icon(
                imageVector = Lucide.EllipsisVertical,
                contentDescription = stringResource(Res.string.compras_actions_menu),
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
        ) {
            DropdownMenuItem(
                text = { Text(stringResource(Res.string.compras_action_view)) },
                leadingIcon = { Icon(Lucide.Eye, contentDescription = null) },
                onClick = {
                    expanded = false
                    onView()
                },
            )
            if (canEdit) {
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.compras_action_edit)) },
                    leadingIcon = { Icon(Lucide.Pencil, contentDescription = null) },
                    onClick = {
                        expanded = false
                        onEdit()
                    },
                )
            }
        }
    }
}
